import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TEAM_AVATARS } from '../teamAvatars';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Sun, CloudSun, Cloud, CloudFog, CloudRain, Snowflake, CloudLightning, Wind,
  Newspaper, MapPin, Lightbulb, ExternalLink, ArrowRight, Search, LogOut, Settings2, Loader2, Megaphone, Zap, Building2, CalendarDays, Bookmark,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const wIcon = (code, cls = 'w-6 h-6') => {
  if (code === 0) return <Sun className={`${cls} text-amber-500`} />;
  if (code <= 2) return <CloudSun className={`${cls} text-amber-400`} />;
  if (code === 3) return <Cloud className={`${cls} text-neutral-400`} />;
  if (code <= 48) return <CloudFog className={`${cls} text-neutral-400`} />;
  if (code <= 67 || (code >= 80 && code <= 82)) return <CloudRain className={`${cls} text-sky-500`} />;
  if (code <= 77) return <Snowflake className={`${cls} text-sky-300`} />;
  return <CloudLightning className={`${cls} text-violet-500`} />;
};

const scoreColor = (t) => (t >= 75 ? 'text-emerald-600' : t >= 50 ? 'text-amber-600' : 'text-red-500');

const SponsoredCard = ({ b, isEs }) => {
  const tones = {
    orange: 'bg-[#F77F11] text-white',
    dark: 'bg-neutral-950 text-white',
    green: 'bg-emerald-700 text-white',
    light: 'bg-white border border-neutral-200 text-neutral-900',
  };
  const sub = b.tone === 'light' ? 'text-neutral-500' : 'text-white/75';
  const external = /^https?:\/\//.test(b.url);
  const inner = (
    <>
      <span className={`inline-flex items-center gap-1 text-[9.5px] tracking-[0.18em] uppercase font-bold ${b.tone === 'light' ? 'text-[#d8651a]' : 'text-white/60'}`}>
        <Megaphone className="w-3 h-3" /> {isEs ? 'Kitxens · Patrocinado' : 'Kitxens · Sponsored'}
      </span>
      <div className="mt-3">
        <p className="text-[16px] font-bold leading-snug">{b.title}</p>
        <p className={`mt-1 text-[12.5px] leading-relaxed ${sub}`}>{b.body}</p>
      </div>
      <span className={`mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold ${b.tone === 'light' ? 'text-[#F77F11]' : 'text-white'} group-hover:gap-2.5 transition-all`}>
        {b.cta} <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </>
  );
  const cls = `group relative h-full rounded-2xl p-5 flex flex-col justify-between overflow-hidden transition-transform hover:-translate-y-0.5 ${tones[b.tone] || tones.dark}`;
  return external ? (
    <a href={b.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`client-banner-${b.id}`}>{inner}</a>
  ) : (
    <Link to={b.url} className={cls} data-testid={`client-banner-${b.id}`}>{inner}</Link>
  );
};

const RotatingSponsored = ({ banners, offset = 0, isEs }) => {
  const [i, setI] = useState(offset % banners.length);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % banners.length), 7000);
    return () => clearInterval(t);
  }, [banners.length]);
  const b = banners[i];
  return (
    <div className="relative min-h-[190px]" data-testid={`client-banner-slot-${offset}`}>
      <AnimatePresence mode="wait">
        <motion.div key={b.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45 }} className="absolute inset-0">
          <SponsoredCard b={b} isEs={isEs} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const ClientLibrary = ({ isEs }) => {
  const [data, setData] = React.useState({ pins: [], folders: {}, streak: 0, digest_paused: false, badges: [], folder_views: {} });
  const [activeFolder, setActiveFolder] = React.useState('');
  const load = React.useCallback(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => j.signed_in && setData({ pins: j.pins || [], folders: j.folders || {}, streak: j.streak || 0, digest_paused: !!j.digest_paused, badges: j.badges || [], folder_views: j.folder_views || {} })).catch(() => {});
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const folderNames = [...new Set(Object.values(data.folders))].sort();
  const shown = activeFolder ? data.pins.filter((p) => data.folders[p.slug] === activeFolder) : data.pins;
  const assignFolder = async (slug, folder) => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/folder`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, folder }),
    });
    const j = await r.json();
    if (j.ok) setData((d) => ({ ...d, folders: j.folders }));
  };
  const shareFolder = async (folder) => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/share-folder`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder }),
    });
    const j = await r.json();
    if (j.ok) {
      const link = `${window.location.origin}${j.url}`;
      try { await navigator.clipboard.writeText(link); } catch { /* noop */ }
      toast.success(isEs ? `Enlace de la carpeta "${folder}" copiado 🗂` : `Folder "${folder}" link copied 🗂`);
    }
  };
  const toggleDigest = async () => {
    const paused = !data.digest_paused;
    setData((d) => ({ ...d, digest_paused: paused }));
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/digest-pref`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paused }),
    });
  };
  if (!data.pins.length) return null;
  return (
    <section className="mt-10" data-testid="client-library-section">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base md:text-lg font-bold text-neutral-900 flex items-center gap-2">
          <Bookmark className="w-5 h-5 text-[#F77F11]" /> {isEs ? 'Tu biblioteca' : 'Your library'}
        </h2>
        {data.streak > 0 && (
          <span data-testid="client-streak-badge" className="inline-flex items-center gap-1 rounded-full bg-neutral-900 text-white text-[11px] font-bold px-2.5 py-1">
            🔥 {data.streak} {isEs ? (data.streak === 1 ? 'semana leyendo' : 'semanas leyendo') : (data.streak === 1 ? 'week streak' : 'weeks streak')}
          </span>
        )}
        {data.badges.map((b) => (
          <span key={b.id} data-testid={`client-badge-${b.id}`} title={b.label}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white text-neutral-700 text-[11px] font-bold px-2.5 py-1">
            {b.icon} {b.label}
          </span>
        ))}
      </div>
      <p className="mt-1 text-[12.5px] text-neutral-500">{isEs ? 'Los artículos de la webzine que guardaste.' : 'The webzine articles you saved.'}</p>
      {folderNames.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="client-folder-chips">
          <button type="button" onClick={() => setActiveFolder('')}
            className={`px-3 py-1 rounded-full text-[12px] font-bold border transition-colors ${!activeFolder ? 'bg-[#F77F11] text-white border-[#F77F11]' : 'bg-white text-neutral-600 border-neutral-200 hover:border-[#F77F11]'}`}>
            {isEs ? 'Todos' : 'All'}
          </button>
          {folderNames.map((f) => (
            <button key={f} type="button" onClick={() => setActiveFolder(f)}
              className={`px-3 py-1 rounded-full text-[12px] font-bold border transition-colors ${activeFolder === f ? 'bg-[#F77F11] text-white border-[#F77F11]' : 'bg-white text-neutral-600 border-neutral-200 hover:border-[#F77F11]'}`}>
              🗂 {f}
            </button>
          ))}
          {activeFolder && (
            <button type="button" onClick={() => shareFolder(activeFolder)} data-testid="client-share-folder-btn"
              className="px-3 py-1 rounded-full text-[12px] font-bold border border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700 transition-colors">
              🔗 {isEs ? 'Compartir carpeta' : 'Share folder'}
            </button>
          )}
          {activeFolder && (data.folder_views[activeFolder] || 0) > 0 && (
            <span data-testid="client-folder-views" className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-600 text-[12px] font-bold px-2.5 py-1">
              👀 {data.folder_views[activeFolder]} {isEs ? 'aperturas' : 'opens'}
            </span>
          )}
        </div>
      )}
      <div className="mt-4 flex gap-4 overflow-x-auto pb-2">
        {shown.map((p) => (
          <div key={p.slug} className="shrink-0 w-56 rounded-2xl border border-neutral-200 bg-white hover:border-[#F77F11] transition-colors overflow-hidden">
            <Link to={`/magazine/${p.slug}`} data-testid={`client-pin-${p.slug}`}>
              {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-24 object-cover" />}
              <div className="p-3 pb-1">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#F77F11]">{p.category}</p>
                <p className="mt-1 text-[13px] font-bold text-neutral-900 leading-snug line-clamp-2">{p.title}</p>
              </div>
            </Link>
            <div className="px-3 pb-2.5">
              <select value={data.folders[p.slug] || ''} data-testid={`client-folder-select-${p.slug}`}
                onChange={(e) => {
                  if (e.target.value === '__new__') {
                    const name = window.prompt(isEs ? 'Nombre de la carpeta (ej. Marketing, Costes):' : 'Folder name (e.g. Marketing, Costs):');
                    if (name && name.trim()) assignFolder(p.slug, name.trim());
                  } else assignFolder(p.slug, e.target.value);
                }}
                className="w-full text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg px-2 py-1 cursor-pointer">
                <option value="">{isEs ? 'Sin carpeta' : 'No folder'}</option>
                {folderNames.map((f) => <option key={f} value={f}>{f}</option>)}
                <option value="__new__">{isEs ? '+ Nueva carpeta…' : '+ New folder…'}</option>
              </select>
            </div>
          </div>
        ))}
      </div>
      <label className="mt-3 inline-flex items-center gap-2.5 cursor-pointer select-none" data-testid="client-digest-toggle">
        <button type="button" role="switch" aria-checked={!data.digest_paused} onClick={toggleDigest}
          className={`relative w-9 h-5 rounded-full transition-colors ${data.digest_paused ? 'bg-neutral-300' : 'bg-[#F77F11]'}`}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${data.digest_paused ? 'left-0.5' : 'left-[18px]'}`} />
        </button>
        <span className="text-[12.5px] text-neutral-600">
          {data.digest_paused
            ? (isEs ? 'Resumen semanal por email pausado' : 'Weekly email digest paused')
            : (isEs ? 'Recibir el resumen semanal de tu biblioteca por email' : 'Receive your weekly library digest by email')}
        </span>
      </label>
    </section>
  );
};

export const ClientDashboard = ({ user, isEs, onEditProfile, onLogout }) => {
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/client/dashboard?lang=${isEs ? 'es' : 'en'}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setData).catch(() => {});
    fetch(`${API}/api/intelligence-feed?limit=12`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setFeed((d?.items || []).filter((it) => !it.lang || it.lang === (isEs ? 'es' : 'en')).slice(0, 6)))
      .catch(() => {});
  }, [isEs]);

  const t = isEs ? {
    hi: (h) => (h < 12 ? 'Buenos días' : h < 20 ? 'Buenas tardes' : 'Buenas noches'),
    news: 'Noticias del sector', zone: (c) => `En tu zona · ${c}`, opps: 'Oportunidades para tu negocio',
    oppsSub: 'Curadas por la IA de Kitxens — se renuevan cada día', score: 'Tu Index AI Score™',
    noScan: 'Aún no has escaneado tu web. Descubre si la IA te recomienda.', scanCta: 'Escanear gratis (30s)',
    rescan: 'Nuevo escaneo', weather: 'Clima', resources: 'Recursos y enlaces útiles',
    map: 'Competencia cercana', localNews: 'Noticias locales', logout: 'Salir', edit: 'Editar perfil',
    compare: 'Compárate con un competidor →', read: 'Leer',
  } : {
    hi: (h) => (h < 12 ? 'Good morning' : h < 20 ? 'Good afternoon' : 'Good evening'),
    news: 'Industry news', zone: (c) => `In your area · ${c}`, opps: 'Opportunities for your business',
    oppsSub: 'Curated by the Kitxens AI — refreshed daily', score: 'Your Index AI Score™',
    noScan: "You haven't scanned your website yet. See if AI recommends you.", scanCta: 'Scan free (30s)',
    rescan: 'New scan', weather: 'Weather', resources: 'Useful resources & links',
    map: 'Nearby competitors', localNews: 'Local news', logout: 'Log out', edit: 'Edit profile',
    compare: 'Compare against a competitor →', read: 'Read',
  };

  if (!data) {
    return <div className="min-h-[50vh] flex items-center justify-center text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const w = data.weather;
  const lastScan = data.scans?.[0];
  const hour = new Date().getHours();

  return (
    <div className="max-w-[1180px] mx-auto px-4 sm:px-6" data-testid="client-dashboard">
      {/* Greeting bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {user.picture
            ? <img src={user.picture} alt="" className="w-11 h-11 rounded-full border border-neutral-200" referrerPolicy="no-referrer" />
            : <span className="w-11 h-11 rounded-full bg-neutral-900 text-white flex items-center justify-center font-bold">{(user.name || user.email || '?')[0]?.toUpperCase()}</span>}
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-neutral-900 truncate" data-testid="client-greeting">
              {t.hi(hour)}, {(user.name || user.email).split(' ')[0]}
            </h1>
            <p className="text-[12.5px] text-neutral-500 truncate">
              {user.restaurant_name} · <MapPin className="inline w-3 h-3 -mt-0.5" /> {user.city}
              {user.is_group && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-neutral-900 text-white px-2 py-0.5 text-[10px] font-bold align-middle" data-testid="client-group-badge">
                  <Building2 className="w-2.5 h-2.5" /> {isEs ? 'Grupo' : 'Group'} · {(user.locations?.length || 0) + 1}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {w && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-neutral-200 px-3.5 py-1.5 text-[13px] font-bold text-neutral-800" data-testid="client-weather-chip">
              <span className="hidden md:inline-flex items-center gap-1 text-neutral-500 font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#F77F11]" />
                <span className="max-w-[130px] truncate">{(user.city || '').split(',')[0]}</span>
                <span className="text-neutral-300 mx-0.5">·</span>
              </span>
              {wIcon(w.code, 'w-4 h-4')} {Math.round(w.temp)}°
              {w.days?.[1] && (
                <span className="hidden lg:inline-flex items-center gap-1 text-neutral-400 font-semibold border-l border-neutral-200 pl-2 ml-1" data-testid="client-weather-tomorrow">
                  {isEs ? 'mañana' : 'tmrw'} {wIcon(w.days[1].code, 'w-3.5 h-3.5')} {Math.round(w.days[1].max)}°
                </span>
              )}
            </span>
          )}
          <button onClick={onEditProfile} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="client-edit-profile-btn">
            <Settings2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t.edit}</span>
          </button>
          <button onClick={onLogout} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="client-logout-btn">
            <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t.logout}</span>
          </button>
        </div>
      </div>

      {/* AI Workforce strip — meet the team that works for you */}
      <Link to="/ai-team" className="mt-5 flex items-center justify-between gap-3 rounded-2xl bg-[#0F1116] hover:bg-neutral-800 transition-colors px-5 py-3.5 group" data-testid="client-ai-team-strip">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex -space-x-2.5 shrink-0">
            {TEAM_AVATARS.slice(0, 5).map((a) => (
              <img key={a.key} src={a.img} alt={a.name} loading="lazy"
                className="w-9 h-9 rounded-full border-2 border-[#0F1116] object-cover bg-neutral-700" />
            ))}
          </div>
          <div className="min-w-0">
            <p className="text-[13.5px] font-bold text-white truncate">
              {isEs ? 'Tu AI Workforce trabaja 24/7' : 'Your AI Workforce works 24/7'}
            </p>
            <p className="text-[11.5px] text-neutral-400 truncate hidden sm:block">
              {isEs ? 'Rachel contesta, Stan capta, Sonny publica, Penny escribe, Eva supervisa' : 'Rachel answers, Stan captures, Sonny posts, Penny writes, Eva supervises'}
            </p>
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#FF9B4A] group-hover:translate-x-0.5 transition-transform">
          {isEs ? 'Conócelos' : 'Meet them'} <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </Link>

      {/* Restaurant AI OS demo — the real product, sample data */}
      <button onClick={async () => {
        try {
          const r = await fetch(`${API}/api/rest/demo/enter`, { method: 'POST', credentials: 'include' });
          if (r.ok) window.location.href = '/restaurant';
        } catch { /* noop */ }
      }}
        className="mt-4 w-full text-left flex items-center justify-between gap-3 rounded-2xl border-2 border-[#F77F11] bg-orange-50/60 hover:bg-orange-50 transition-colors px-5 py-4 group" data-testid="client-os-demo-strip">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#d8651a]">{isEs ? 'Nuevo · Restaurant AI OS' : 'New · Restaurant AI OS'}</p>
          <p className="mt-1 text-[14.5px] font-bold text-neutral-900">
            {isEs ? 'Explora el sistema operativo completo de un restaurante — en vivo, con datos de muestra' : 'Explore the complete restaurant operating system — live, with sample data'}
          </p>
          <p className="text-[12px] text-neutral-500 hidden sm:block">
            {isEs ? 'Mission Control, Revenue, Delivery, AI Executive… el mismo producto que usan nuestros clientes.' : 'Mission Control, Revenue, Delivery, AI Executive… the exact product our customers run.'}
          </p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#F77F11] group-hover:bg-[#d8651a] text-white px-4 py-2 text-[13px] font-bold transition-colors">
          {isEs ? 'Entrar a la demo' : 'Launch the demo'} <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </button>

      {/* Smart actions — what to do TODAY */}
      {(data.smart_actions || []).length > 0 && (
        <section className="mt-6" data-testid="client-smart-actions">
          <h2 className="text-base md:text-lg font-bold text-neutral-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#F77F11]" /> {isEs ? 'Acciones para hoy' : "Today's actions"}
          </h2>
          <p className="mt-1 text-[12.5px] text-neutral-500">
            {isEs ? 'Recomendaciones de tu equipo AI según el clima, el día y tus datos' : 'Recommendations from your AI team based on weather, day and your data'}
          </p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.smart_actions.map((a) => (
              <div key={a.id}
                className={`rounded-2xl p-5 flex flex-col justify-between border-2 ${a.urgency === 'hot' ? 'border-[#F77F11] bg-orange-50/50' : 'border-neutral-200 bg-white'}`}
                data-testid={`client-action-${a.id}`}>
                <div>
                  <span className={`inline-flex items-center gap-1 text-[9.5px] tracking-[0.16em] uppercase font-bold ${a.urgency === 'hot' ? 'text-[#d8651a]' : 'text-neutral-400'}`}>
                    <Zap className="w-3 h-3" /> {a.agent} · {a.urgency === 'hot' ? (isEs ? 'Hoy' : 'Today') : (isEs ? 'Esta semana' : 'This week')}
                  </span>
                  <p className="mt-2 text-[15px] font-bold text-neutral-900 leading-snug">{a.title}</p>
                  <p className="mt-1.5 text-[12.5px] text-neutral-600 leading-relaxed">{a.body}</p>
                </div>
                <Link to={a.url}
                  className={`mt-4 inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full text-[13px] font-bold transition-colors ${a.urgency === 'hot' ? 'bg-[#F77F11] hover:bg-[#d8651a] text-white' : 'bg-neutral-900 hover:bg-neutral-700 text-white'}`}>
                  {a.cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sales calendar — anticipate the big dates */}
      {(data.sales_calendar || []).length > 0 && (
        <section className="mt-8" data-testid="client-sales-calendar">
          <h2 className="text-base md:text-lg font-bold text-neutral-900 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#F77F11]" /> {isEs ? 'Calendario de ventas' : 'Sales calendar'}
          </h2>
          <p className="mt-1 text-[12.5px] text-neutral-500">
            {isEs ? 'Las próximas fechas que llenan restaurantes — anticípate y véndelas' : 'The upcoming dates that fill restaurants — get ahead and sell them'}
          </p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.sales_calendar.map((h) => (
              <div key={`${h.date}-${h.name}`} className={`rounded-2xl border-2 p-5 ${h.days_left <= 14 ? 'border-[#F77F11] bg-orange-50/50' : 'border-neutral-200 bg-white'}`} data-testid="client-calendar-item">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[15px] font-bold text-neutral-900 leading-snug">{h.name}</p>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold tabular-nums ${h.days_left <= 14 ? 'bg-[#F77F11] text-white' : 'bg-neutral-100 text-neutral-600'}`}>
                    {h.days_left === 0 ? (isEs ? '¡HOY!' : 'TODAY!') : `${h.days_left} ${isEs ? 'días' : 'days'}`}
                  </span>
                </div>
                <p className="mt-0.5 text-[11.5px] text-neutral-400 tabular-nums">
                  {new Date(h.date + 'T12:00:00').toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="mt-2 text-[12.5px] text-neutral-600 leading-relaxed">💡 {h.tip}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Row 1: score + weather + sponsored */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid="client-score-card">
          <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-neutral-500 flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5 text-[#F77F11]" /> {t.score}
          </p>
          {lastScan ? (
            <>
              <div className="mt-3 flex items-end gap-2">
                <span className={`text-4xl font-bold ${scoreColor(lastScan.total)}`}>{lastScan.total}</span>
                <span className="text-[13px] text-neutral-400 font-semibold mb-1">/ 100 · {lastScan.domain}</span>
              </div>
              <div className="mt-3 flex items-center gap-4">
                <Link to="/index-ai#scan" className="text-[13px] font-bold text-[#F77F11] hover:underline underline-offset-4">{t.rescan} →</Link>
                <Link to="/index-ai#compare" className="text-[13px] font-semibold text-neutral-500 hover:text-neutral-900">{t.compare}</Link>
              </div>
            </>
          ) : (
            <>
              <p className="mt-3 text-[13.5px] text-neutral-600 leading-relaxed">{t.noScan}</p>
              <Link to="/index-ai#scan" className="mt-3 inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-10 px-5 rounded-full text-[13px] font-bold transition-colors" data-testid="client-scan-cta">
                {t.scanCta} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid="client-weather-card">
          <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-neutral-500">{t.weather} · {user.city}</p>
          {w ? (
            <>
              <div className="mt-3 flex items-center gap-3">
                {wIcon(w.code, 'w-10 h-10')}
                <span className="text-4xl font-bold text-neutral-900">{Math.round(w.temp)}°C</span>
                <span className="inline-flex items-center gap-1 text-[12px] text-neutral-400 font-semibold"><Wind className="w-3.5 h-3.5" /> {Math.round(w.wind)} km/h</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {(w.days || []).slice(0, 3).map((d) => (
                  <div key={d.date} className="rounded-xl bg-neutral-50 border border-neutral-100 px-2 py-2 text-center">
                    <p className="text-[10.5px] font-bold text-neutral-400 uppercase">{new Date(d.date + 'T12:00').toLocaleDateString(isEs ? 'es' : 'en', { weekday: 'short' })}</p>
                    <div className="mt-1 flex justify-center">{wIcon(d.code, 'w-4 h-4')}</div>
                    <p className="mt-1 text-[11.5px] font-bold text-neutral-700">{Math.round(d.max)}° <span className="text-neutral-400 font-medium">{Math.round(d.min)}°</span></p>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="mt-3 text-[13px] text-neutral-400">—</p>}
        </div>
        <RotatingSponsored banners={data.banners} offset={0} isEs={isEs} />
      </div>

      {/* Reader library — pinned magazine articles */}
      <ClientLibrary isEs={isEs} />

      {/* Industry news */}
      {feed.length > 0 && (
        <section className="mt-10" data-testid="client-news-section">
          <h2 className="text-base md:text-lg font-bold text-neutral-900 flex items-center gap-2"><Newspaper className="w-4.5 h-4.5 w-5 h-5 text-[#F77F11]" /> {t.news}</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {feed.slice(0, 5).map((it) => {
              const external = /^https?:\/\//.test(it.url || '');
              const title = isEs ? (it.title_es || it.title) : it.title;
              const inner = (
                <>
                  <p className="text-[14px] font-bold text-neutral-900 leading-snug group-hover:text-[#d8651a] transition-colors">{title}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#F77F11]">
                    {(isEs ? (it.cta_es || t.read) : (it.cta || t.read))} {external ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                  </span>
                </>
              );
              return external ? (
                <a key={it.id} href={`/go?u=${encodeURIComponent(it.url)}`} target="_blank" rel="noopener noreferrer"
                  className="group rounded-2xl bg-white border border-neutral-200 p-5 flex flex-col justify-between hover:-translate-y-0.5 transition-transform">
                  {inner}
                </a>
              ) : (
                <Link key={it.id} to={it.url || '/operating-intelligence'}
                  className="group rounded-2xl bg-white border border-neutral-200 p-5 flex flex-col justify-between hover:-translate-y-0.5 transition-transform">
                  {inner}
                </Link>
              );
            })}
            <RotatingSponsored banners={data.banners} offset={2} isEs={isEs} />
          </div>
        </section>
      )}

      {/* In your area */}
      <section className="mt-10" data-testid="client-zone-section">
        <h2 className="text-base md:text-lg font-bold text-neutral-900 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#F77F11]" /> {t.zone(user.city)}</h2>
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid="client-local-news">
            <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-neutral-500">{t.localNews}</p>
            <div className="mt-3 divide-y divide-neutral-100">
              {(data.local_news || []).map((n, i) => (
                <a key={i} href={`/go?u=${encodeURIComponent(n.url)}`} target="_blank" rel="noopener noreferrer" className="group block py-2.5">
                  <p className="text-[13.5px] font-semibold text-neutral-800 leading-snug group-hover:text-[#d8651a] transition-colors">{n.title}</p>
                  <p className="mt-0.5 text-[11px] text-neutral-400">{n.source} {n.date && `· ${n.date}`}</p>
                </a>
              ))}
              {(data.local_news || []).length === 0 && <p className="py-3 text-[13px] text-neutral-400">—</p>}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden flex flex-col" data-testid="client-map-card">
            <p className="px-5 pt-5 text-[10.5px] tracking-[0.18em] uppercase font-bold text-neutral-500">{t.map}</p>
            {data.map_q && (
              <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent(data.map_q)}&output=embed`}
                className="mt-3 w-full flex-1 min-h-[260px] border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            )}
          </div>
        </div>
        {(data.resources || []).length > 0 && (
          <div className="mt-4 rounded-2xl bg-white border border-neutral-200 p-5" data-testid="client-resources">
            <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-neutral-500">{t.resources}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.resources.map((r, i) => (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 hover:border-[#F77F11] bg-neutral-50 hover:bg-orange-50 px-3.5 py-2 text-[12.5px] font-semibold text-neutral-700 hover:text-[#d8651a] transition-colors"
                  title={r.desc}>
                  {r.label} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Opportunities */}
      <section className="mt-10" data-testid="client-opps-section">
        <h2 className="text-base md:text-lg font-bold text-neutral-900 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-[#F77F11]" /> {t.opps}</h2>
        <p className="mt-1 text-[12.5px] text-neutral-500">{t.oppsSub}</p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data.opportunities || []).map((o, i) => (
            <div key={i} className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid={`client-opp-${i}`}>
              <p className="text-[14px] font-bold text-neutral-900 leading-snug">{o.title}</p>
              <p className="mt-1.5 text-[12.5px] text-neutral-500 leading-relaxed">{o.body}</p>
            </div>
          ))}
          <RotatingSponsored banners={data.banners} offset={4} isEs={isEs} />
        </div>
      </section>
    </div>
  );
};
